// Blog content is sourced live from the JustGold CMS (see lib/blogApi.ts).
// This module adapts the raw CMS record shape into the UI-facing `BlogPost`
// shape the blog components render, and derives a couple of fields the CMS
// doesn't provide (excerpt, category — see `toBlogPost` below).

import {
  getAllBlogsCached,
  getBlogBySlugCached,
  type RawBlogRecord,
} from "./blogApi";

export type BlogCategory =
  | "Market Insights"
  | "Gold Investing"
  | "Digital Gold"
  | "Guides";

export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  /** Raw CMS HTML body — rendered with html-react-parser in BlogArticle. */
  content: string;
  category: BlogCategory;
  date: string;
  image: string;
}

const FALLBACK_IMAGE = "/blogs/blog1.png";

// ── Derivation helpers ───────────────────────────────────────────────────────
// The CMS does not provide excerpt/category fields, so we derive them here.

/** Strip HTML tags from CMS content, collapse whitespace, and truncate. */
export function excerptFromContent(html: string | undefined, maxLength = 160): string {
  if (!html) return "";
  const text = html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();

  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trimEnd()}…`;
}

/**
 * Heuristic keyword classifier — NOT CMS-sourced truth. The CMS has no
 * category field, but the existing UI (BlogIndex filter pills, BlogArticle
 * badge) expects one of the four fixed buckets below. This is a best-effort
 * cosmetic bucketing based on `meta_keywords` + `title` signal words; it's
 * approximate by design and fine to be occasionally "wrong" since nothing
 * factual hinges on it.
 */
export function classifyCategory(
  title: string,
  metaKeywords: string[] | undefined
): BlogCategory {
  const haystack = [title, ...(metaKeywords ?? [])].join(" ").toLowerCase();

  if (/digital gold|digital ownership|\bdigital\b/.test(haystack)) {
    return "Digital Gold";
  }
  if (/\bguide\b|how to|comprehensive guide|step by step/.test(haystack)) {
    return "Guides";
  }
  if (/invest|portfolio|trust|savings?|wealth|save\b/.test(haystack)) {
    return "Gold Investing";
  }
  return "Market Insights";
}

function formatDate(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(d);
}

/** Adapts a raw CMS blog record into the UI-facing BlogPost shape. */
export function toBlogPost(raw: RawBlogRecord): BlogPost {
  const title = raw.title ?? "";
  const metaKeywords = raw.meta_keywords;
  return {
    slug: raw.slug,
    title,
    excerpt: excerptFromContent(raw.content),
    content: raw.content ?? "",
    category: classifyCategory(title, metaKeywords),
    date: formatDate(raw.datetime),
    image: raw.featured_image || FALLBACK_IMAGE,
  };
}

// ── Fetching ─────────────────────────────────────────────────────────────────

/**
 * All published posts, newest first. Falls back to an empty list if the CMS
 * is unreachable, so an API outage never breaks the build or a page render
 * (same defensive pattern as app/sitemap.ts around getAllBlogsServer).
 */
export async function getAllPosts(): Promise<BlogPost[]> {
  try {
    const result = await getAllBlogsCached();
    const list = (Array.isArray(result) ? result : result?.data ?? []) as RawBlogRecord[];

    return list
      .filter((raw) => raw?.status === "Published" && Boolean(raw?.slug))
      .sort((a, b) => {
        // Sort by the original ISO datetime, newest first.
        const bTime = b.datetime ? new Date(b.datetime).getTime() : 0;
        const aTime = a.datetime ? new Date(a.datetime).getTime() : 0;
        return bTime - aTime;
      })
      .map(toBlogPost);
  } catch {
    return [];
  }
}

// Internal detail route for each post.
export function postHref(post: Pick<BlogPost, "slug">) {
  return `/blogs/${post.slug}`;
}

export async function getPostBySlug(slug: string): Promise<BlogPost | undefined> {
  try {
    const raw = await getBlogBySlugCached(slug);
    if (!raw || raw.status !== "Published") return undefined;
    return toBlogPost(raw as RawBlogRecord);
  } catch {
    return undefined;
  }
}

// Related posts: prefer the same category, then fill with the newest others.
export async function getRelatedPosts(slug: string, limit = 3): Promise<BlogPost[]> {
  const all = await getAllPosts();
  const current = all.find((p) => p.slug === slug);
  if (!current) return all.slice(0, limit);

  const sameCategory = all.filter(
    (p) => p.slug !== slug && p.category === current.category
  );
  const others = all.filter(
    (p) => p.slug !== slug && p.category !== current.category
  );

  return [...sameCategory, ...others].slice(0, limit);
}

export const BLOG_CATEGORIES: BlogCategory[] = [
  "Market Insights",
  "Gold Investing",
  "Digital Gold",
  "Guides",
];
